// src/pages/ServicesPage.js
import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
// We only import what we need from our simplified data file
import { services, filterCategories } from '../data/servicesData'; 
import '../components/Services.css'; // Assuming you have a CSS file for styling
import PageTransition from '../components/PageTransition';

// --- Service Card Component (Inline for simplicity) ---
const ServiceCard = ({ service }) => (
    <Link to={`/product/${service.id}`} className="card">
        <div className="card-image">
            <img src={service.imageUrl} alt={service.title} />
        </div>
        <div className="card-content">
            <p className="card-meta">{service.temple}</p>
            <h3 className="card-title">{service.title}</h3>
            <p className="card-description">{service.description}</p>
            <div className="btn-service-card full-width">
                <span className="price">₹{service.price}</span>
                <span className="book-now-text">Book Now</span>
            </div>
        </div>
    </Link>
);

// --- Service Section Component (Replacing ServiceSection.js) ---
const CarouselServiceSection = ({ type, heading, services }) => {
    const scrollRef = useRef(null);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 350; // Adjust based on card width + gap
            const currentScroll = scrollRef.current.scrollLeft;

            if (direction === 'left') {
                scrollRef.current.scrollTo({
                    left: currentScroll - scrollAmount,
                    behavior: 'smooth',
                });
            } else {
                scrollRef.current.scrollTo({
                    left: currentScroll + scrollAmount,
                    behavior: 'smooth',
                });
            }
        }
    };

    if (services.length === 0) return null;

    return (
        <section className="service-section">
            <div className="carousel-nav">
                <h2 className="service-heading">{heading}</h2>
                <div>
                    {/* Left Scroll Button */}
                    <button 
                        className="nav-btn" 
                        onClick={() => scroll('left')}
                        aria-label={`Scroll Left on ${heading}`}
                    >
                        &lt;
                    </button>
                    {/* Right Scroll Button */}
                    <button 
                        className="nav-btn" 
                        onClick={() => scroll('right')}
                        aria-label={`Scroll Right on ${heading}`}
                    >
                        &gt;
                    </button>
                </div>
            </div>
            {/* The scrollable grid container */}
            <div className="service-grid" ref={scrollRef}>
                {services.map(service => (
                    <ServiceCard key={service.id} service={service} />
                ))}
            </div>
        </section>
    );
};


// --- Main Services Page Component ---
function ServicesPage() {
    const [activeFilter, setActiveFilter] = useState('all');

    // 1. Logic to group ALL services by type (archana, abhishekam, etc.)
    const groupedServices = useMemo(() => {
        return services.reduce((acc, service) => {
            const type = service.type;
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push(service);
            return acc;
        }, {});
    }, []);

    // Helper to get the display heading label for the section
    const getSectionHeading = (target) => {
        const category = filterCategories.find(c => c.target === target);
        return category ? category.label : '';
    };

    return (
        // Note: The classes like 'background-light' should be consistent with your App.js/index.css
        <div className="background-light dark-mode flex flex-col min-h-screen"> 
            {/* <Header /> */}
            
            <main className="main flex-grow container mx-auto px-6 py-10">
                
                {/* Section Intro */}
                <div className="section-intro">
                    <h2 className="section-title">Our Sacred Services</h2>
                    <p className="section-subtitle">Explore our range of services designed to connect you with divine experiences and ancient traditions, performed on your behalf.</p>
                </div>

                {/* Filter Bar */}
                <div className="filter-bar">
                    {filterCategories.map(cat => (
                        <button
                            key={cat.target}
                            className={`filter-btn ${activeFilter === cat.target ? 'active' : ''}`}
                            onClick={() => setActiveFilter(cat.target)}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Service Sections/Carousels */}
                <div className="filterable-content">
                    {Object.keys(groupedServices).map(serviceType => (
                         <div
                            key={serviceType}
                            className={`filterable-section ${
                                activeFilter === 'all' || activeFilter === serviceType
                                    ? 'active-section'
                                    : ''
                            }`}
                        >
                            <CarouselServiceSection
                                type={serviceType}
                                heading={getSectionHeading(serviceType)}
                                services={groupedServices[serviceType] || []}
                            />
                        </div>
                    ))}
                </div>
                
            </main>
            {/* <Footer /> */}
        </div>
    );
};

export default PageTransition(ServicesPage);
